import { BASE_URL } from "@api/constant";
import {
  FB_APP_ID,
  IOS_APP_STORE_ID,
  ITUNES_APP,
  OG_SITE_NAME,
  ROBOTS,
} from "@config/MetaTagsConstants";
import type { GADataProps } from "@helper/getGtmData";
import { LatestVideoResponse } from "@helper/transformer/useBrightcoveResponse";
import { ProcessedArticleData } from "@helper/transformer/useOsResponse";
import { formatDate } from "@helper/useDateFormat";
import { getAuthorsName, getTagsName } from "@routes/Article/ArticleMetaData";
import { MetaTagsProps } from "@sphtech/web2-seo-schema";
import { ProcessedArticleBody } from "@typings/Opensearch";
import type { PageMetaDataValues } from "@typings/PageMetaData";
import {
  AdsCardProps,
  FinishCardProps,
  QuizCardProps,
} from "@typings/QuickNews";

// Union type shared across this module
type QuickNewsCard =
  | ProcessedArticleData
  | QuizCardProps
  | AdsCardProps
  | FinishCardProps
  | LatestVideoResponse;

type CardsMetaDataProps = {
  // Optional: an empty QuickNews feed has no first card.
  card?: QuickNewsCard;
  activeTab: string;
  onBoarded: boolean;
  pagination: number;
};

// Module-level constants — never change at runtime
const DEFAULT_META_TITLE =
  "முன்னோட்டம்: அண்மைச் செய்திகள் - Quick News & Shorts by Tamil Murasu";
const DEFAULT_META_DESC =
  "Catch latest shorts & easy-to-read news. Quick News (முன்னோட்டம்) has அரசியல், சமூகம், பிரபலமான தலைப்புச் செய்திகள், காணொளிகள்; சுருக்கமாக, வேகமாக.";
const DEFAULT_META_SLUG = "quick-news/";
const ADDITIONAL_META_DESCRIPTORS = [
  { tagName: "link", rel: "manifest", href: "/manifest.json" },
];

export default function QuickNewsMetaData({
  card,
  activeTab,
  onBoarded,
  pagination,
}: CardsMetaDataProps): PageMetaDataValues {
  const cardId = card?.id ?? "";
  const cardTitle =
    (card && "title" in card && card.title) ||
    (card && "name" in card && card.name) ||
    "";
  // null when card is an ad, quiz, or absent — safe to cast the rest as ProcessedArticleData
  const articleCard =
    card &&
    "id" in card &&
    "title" in card &&
    !("adType" in card || "quizzes" in card)
      ? (card as ProcessedArticleData)
      : null;

  /* -----------------------------
   * GA DATA
   * ----------------------------- */
  const baseGaData = { level2: "quick-news", contentcat: 1 } as const;

  const gaData: GADataProps = onBoarded
    ? {
        ...baseGaData,
        title: cardTitle,
        articleid: articleCard ? cardId : "",
        cue_articleid: articleCard ? cardId : "",
        contenttype: "1",
        pagination: pagination + 1,
        author: articleCard ? getAuthorsName(articleCard.authors || []) : "",
        pubdate: articleCard?.datetime
          ? formatDate(articleCard.datetime, "yyyy-MM-dd HH:mm:ss")
          : "",
        content_publication_date_utc: articleCard?.datetime ?? "",
        content_update_date_utc: articleCard?.dateEdited ?? "",
        keyword: articleCard ? getTagsName(articleCard.tags || []) : "",
      }
    : {
        ...baseGaData,
        title: "quick_news_index",
        contenttype: undefined,
        pagination: 0,
      };

  /* -----------------------------
   * META TAGS
   * ----------------------------- */
  const metaTitle = onBoarded ? cardTitle : DEFAULT_META_TITLE;
  const metaDesc = onBoarded
    ? getMetaDescription(card, activeTab) || ""
    : DEFAULT_META_DESC;
  const metaSlug = onBoarded
    ? `quick-news/${activeTab === "Videos" ? `video/${cardId}` : cardId}`
    : DEFAULT_META_SLUG;
  const metaImage =
    (activeTab === "MyFeed" &&
      card &&
      "imageField" in card &&
      card.imageField?.url) ||
    (activeTab === "Videos" && card && "thumbnail" in card && card.thumbnail) ||
    "";

  const metaTags: MetaTagsProps = {
    title: metaTitle,
    description: metaDesc,
    slug: metaSlug,
    image: metaImage,
    robots: ROBOTS,
    fbAppId: FB_APP_ID,
    iosAppStoreId: IOS_APP_STORE_ID,
    appleItunesAppId: ITUNES_APP,
    urlOrigin: BASE_URL,
    ogTitle: metaTitle,
    ogDescription: metaDesc,
    ogSiteName: OG_SITE_NAME,
    additionalMetaDescriptors: ADDITIONAL_META_DESCRIPTORS,
  };

  return { gaData, metaTags };
}

/* -----------------------------------------------------
 * Helper: build meta description from card content
 * ----------------------------------------------------- */
function getMetaDescription(
  card: QuickNewsCard | undefined,
  activeTab: string,
): string {
  if (card && "articleBody" in card) {
    const text = getFirstTwoLines(card.articleBody);
    if (text) return text;
  }
  if (activeTab === "Videos" && card && "description" in card) {
    return card.description || "";
  }
  return "";
}

/* -----------------------------------------------------
 * Helper: extract first two paragraph lines from article body
 * ----------------------------------------------------- */
export function getFirstTwoLines(
  articleBody: ProcessedArticleBody | null,
): string {
  if (!articleBody) return "";
  return articleBody
    .filter((el) => el.type === "paragraph")
    .flatMap((para) => (para.value ? para.value.split("\n") : []))
    .slice(0, 2)
    .join("\n");
}
